import React, { Component } from 'react';
import axios from 'axios';
import { Modal } from 'react-bootstrap';
import { authMiddleWare } from '../utilities/Auth';
import '../pages/css/support.css';

const CATEGORIES = ['Question', 'Bug', 'Feature Request', 'Billing', 'Account', 'Other'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];

class Support extends Component {
    state = {
        tickets: [],
        loading: true,
        saving: false,
        error: '',
        showCreate: false,
        showTicket: false,
        selectedTicket: null,
        messages: [],
        title: '',
        category: 'Question',
        priority: 'Medium',
        description: '',
        reply: ''
    };

    componentDidMount() {
        this.loadTickets();
    }

    configureAuth = () => {
        authMiddleWare(this?.props?.history);
        const authToken = localStorage.getItem('AuthToken');
        const refreshToken = localStorage.getItem('RefreshToken');
        axios.defaults.headers.common = { Authorization: `${authToken}`, RefreshToken: refreshToken };
    };

    persistRefreshedTokens = (response) => {
        if (response?.headers?.authtoken && response?.headers?.refreshtoken) {
            localStorage.setItem('AuthToken', `Bearer ${response.headers.authtoken}`);
            localStorage.setItem('RefreshToken', response.headers.refreshtoken);
        }
    };

    handleApiError = (error, fallback) => {
        console.log(error);
        if (error?.response?.status === 403 || error?.response?.status === 401) {
            localStorage.setItem('SessionExpired', 'Y');
            window.location.href = '/login';
            return;
        }
        this.setState({ error: error?.response?.data?.error || fallback });
    };

    loadTickets = () => {
        this.configureAuth();
        this.setState({ loading: true, error: '' });
        axios.get('/api/support/tickets')
            .then((response) => {
                this.persistRefreshedTokens(response);
                this.setState({ tickets: response.data?.tickets || [], loading: false });
            })
            .catch((error) => {
                this.setState({ loading: false });
                this.handleApiError(error, 'Unable to load support tickets.');
            });
    };

    openCreate = () => {
        this.setState({
            showCreate: true,
            error: '',
            title: '',
            category: 'Question',
            priority: 'Medium',
            description: ''
        });
    };

    createTicket = (event) => {
        event.preventDefault();
        if (!this.state.title.trim() || !this.state.description.trim()) {
            this.setState({ error: 'Title and description are required.' });
            return;
        }

        this.configureAuth();
        this.setState({ saving: true, error: '' });
        axios.post('/api/support/tickets', {
            title: this.state.title,
            category: this.state.category,
            priority: this.state.priority,
            description: this.state.description
        })
            .then((response) => {
                this.persistRefreshedTokens(response);
                this.setState({ showCreate: false, saving: false });
                this.loadTickets();
            })
            .catch((error) => {
                this.setState({ saving: false });
                this.handleApiError(error, 'Unable to create support ticket.');
            });
    };

    openTicket = (ticket) => {
        this.configureAuth();
        this.setState({ loading: true, error: '', reply: '' });
        axios.get(`/api/support/tickets/${ticket.id}`)
            .then((response) => {
                this.persistRefreshedTokens(response);
                this.setState({
                    selectedTicket: response.data.ticket,
                    messages: response.data.messages || [],
                    showTicket: true,
                    loading: false
                });
                this.loadTickets();
            })
            .catch((error) => {
                this.setState({ loading: false });
                this.handleApiError(error, 'Unable to load the support ticket.');
            });
    };

    sendReply = (event) => {
        event.preventDefault();
        if (!this.state.reply.trim() || !this.state.selectedTicket) return;

        this.configureAuth();
        this.setState({ saving: true, error: '' });
        axios.post(`/api/support/tickets/${this.state.selectedTicket.id}/replies`, { message: this.state.reply })
            .then((response) => {
                this.persistRefreshedTokens(response);
                this.setState({
                    selectedTicket: response.data.ticket,
                    messages: response.data.messages || [],
                    reply: '',
                    saving: false
                });
                this.loadTickets();
            })
            .catch((error) => {
                this.setState({ saving: false });
                this.handleApiError(error, 'Unable to send your reply.');
            });
    };

    formatDate = (value) => {
        if (!value) return '';
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
    };

    prettyStatus = (status) => String(status || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

    render() {
        return (
            <div className="gorepa-support-page">
                <div className="gorepa-support-header">
                    <div>
                        <h1>Support Tickets</h1>
                        <p>Get help with bugs, questions, billing, or feature requests.</p>
                    </div>
                    <button className="btn btn-primary gorepa-support-new-button" onClick={this.openCreate}>
                        <i className="fas fa-plus"></i>&nbsp;&nbsp;New Ticket
                    </button>
                </div>

                {this.state.error && <div className="alert alert-danger gorepa-support-alert">{this.state.error}</div>}

                {this.state.loading && !this.state.showTicket ? (
                    <div className="gorepa-support-empty"><i className="fas fa-circle-notch fa-spin"></i>&nbsp; Loading support tickets...</div>
                ) : this.state.tickets.length === 0 ? (
                    <div className="gorepa-support-empty">
                        <i className="far fa-life-ring"></i>
                        <h3>No support tickets yet</h3>
                        <p>If you need help, create a ticket and our team will get back to you.</p>
                        <button className="btn btn-primary" onClick={this.openCreate}>Create Your First Ticket</button>
                    </div>
                ) : (
                    <div className="gorepa-support-list">
                        {this.state.tickets.map((ticket) => (
                            <div className="gorepa-support-ticket-card" key={ticket.id} onClick={() => this.openTicket(ticket)}>
                                <div className="gorepa-support-ticket-main">
                                    <div className="gorepa-support-ticket-title-row">
                                        <h3>{ticket.title}</h3>
                                        <span className={`gorepa-ticket-status status-${ticket.status}`}>{this.prettyStatus(ticket.status)}</span>
                                    </div>
                                    <div className="gorepa-support-ticket-description">{ticket.description}</div>
                                    <div className="gorepa-support-ticket-meta">
                                        <span className="gorepa-ticket-chip">{ticket.category}</span>
                                        <span className={`gorepa-ticket-chip priority-${String(ticket.priority || '').toLowerCase()}`}>{ticket.priority} priority</span>
                                        <span>{ticket.ticketNumber}</span>
                                        <span>{this.formatDate(ticket.updatedAt)}</span>
                                        {Number(ticket.userUnreadCount || 0) > 0 && <span className="gorepa-ticket-unread">{ticket.userUnreadCount} new</span>}
                                    </div>
                                </div>
                                <button className="btn btn-secondary gorepa-support-view-button">View</button>
                            </div>
                        ))}
                    </div>
                )}

                <Modal show={this.state.showCreate} onHide={() => this.setState({ showCreate: false, error: '' })} centered size="lg" dialogClassName="gorepa-support-modal">
                    <Modal.Header closeButton>
                        <Modal.Title>Create Support Ticket</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <form onSubmit={this.createTicket}>
                            <div className="form-group">
                                <label>Title *</label>
                                <input className="form-control" maxLength="160" value={this.state.title} onChange={(e) => this.setState({ title: e.target.value })} placeholder="Brief description of your issue" />
                            </div>
                            <div className="gorepa-support-form-grid">
                                <div className="form-group">
                                    <label>Category *</label>
                                    <select className="form-control" value={this.state.category} onChange={(e) => this.setState({ category: e.target.value })}>
                                        {CATEGORIES.map((item) => <option value={item} key={item}>{item}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Priority *</label>
                                    <select className="form-control" value={this.state.priority} onChange={(e) => this.setState({ priority: e.target.value })}>
                                        {PRIORITIES.map((item) => <option value={item} key={item}>{item}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Description *</label>
                                <textarea className="form-control gorepa-support-textarea" maxLength="10000" value={this.state.description} onChange={(e) => this.setState({ description: e.target.value })} placeholder="Provide as much detail as possible about your issue..." />
                            </div>
                            <div className="gorepa-support-modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => this.setState({ showCreate: false, error: '' })}>Cancel</button>
                                <button type="submit" className="btn btn-primary" disabled={this.state.saving}>{this.state.saving ? 'Creating...' : 'Create Ticket'}</button>
                            </div>
                        </form>
                    </Modal.Body>
                </Modal>

                <Modal show={this.state.showTicket} onHide={() => this.setState({ showTicket: false, selectedTicket: null, error: '' })} centered size="lg" dialogClassName="gorepa-support-modal">
                    <Modal.Header closeButton>
                        <Modal.Title>{this.state.selectedTicket?.title || 'Support Ticket'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {this.state.selectedTicket && (
                            <>
                                <div className="gorepa-support-detail-meta">
                                    <span className="gorepa-ticket-chip">{this.state.selectedTicket.category}</span>
                                    <span className={`gorepa-ticket-chip priority-${String(this.state.selectedTicket.priority || '').toLowerCase()}`}>{this.state.selectedTicket.priority} priority</span>
                                    <span className={`gorepa-ticket-status status-${this.state.selectedTicket.status}`}>{this.prettyStatus(this.state.selectedTicket.status)}</span>
                                    <span>{this.state.selectedTicket.ticketNumber}</span>
                                </div>

                                <div className="gorepa-support-conversation">
                                    {this.state.messages.map((message) => (
                                        <div className={`gorepa-support-message ${message.authorType === 'admin' ? 'admin-message' : 'user-message'}`} key={message.id}>
                                            <div className="gorepa-support-message-header">
                                                <b>{message.authorType === 'admin' ? 'GoRepa Support' : message.authorName}</b>
                                                <span>{this.formatDate(message.createdAt)}</span>
                                            </div>
                                            <div className="gorepa-support-message-body">{message.body}</div>
                                        </div>
                                    ))}
                                </div>

                                {this.state.selectedTicket.status !== 'closed' ? (
                                    <form onSubmit={this.sendReply} className="gorepa-support-reply-form">
                                        <label>Add a reply</label>
                                        <textarea className="form-control" value={this.state.reply} onChange={(e) => this.setState({ reply: e.target.value })} placeholder="Type your message..." />
                                        <button className="btn btn-primary" type="submit" disabled={this.state.saving || !this.state.reply.trim()}>{this.state.saving ? 'Sending...' : 'Send Reply'}</button>
                                    </form>
                                ) : (
                                    <div className="gorepa-support-closed-message">This ticket is closed. Create a new ticket if you need additional assistance.</div>
                                )}
                            </>
                        )}
                    </Modal.Body>
                </Modal>
            </div>
        );
    }
}

export default Support;
